/**
 * Cost by source — stacked bars across the 7-day window. Each bar is
 * split into vendor vs infra. Vendor cost is the per-audit AI-engine
 * spend rolled up by aggregateEngineCosts and attached to the
 * audit_cost_summary log line as vendor_total_cost_in_currency.
 */
import { ViewQueryButton } from "./ViewQueryButton";
import { formatInt, formatUsd } from "./format";
import type { ApiEnvelope, BySourceData } from "./types";

interface CostBySourceChartProps {
  envelope: ApiEnvelope<BySourceData> | undefined;
  loading: boolean;
}

export function CostBySourceChart({ envelope, loading }: CostBySourceChartProps) {
  const data = envelope?.data;
  const rows = data?.rows ?? [];
  const max = Math.max(...rows.map((r) => r.total_cost), 0.0001);
  const totalAudits = rows.reduce((sum, r) => sum + r.audit_count, 0);
  const totalInfra = rows.reduce((sum, r) => sum + r.infra_cost, 0);
  const totalVendor = rows.reduce((sum, r) => sum + r.vendor_cost, 0);

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Cost by source (7d)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Stacked vendor (orange) + infra (blue) per audit source.
          </p>
        </div>
        <div className="text-right text-xs text-zinc-500">
          {formatInt(totalAudits)} audits • {formatUsd(totalInfra)} infra
          {totalVendor > 0 && <> • {formatUsd(totalVendor)} vendor</>}
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500">loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-500">
          No audits in the last 7 days.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const totalPct = (r.total_cost / max) * 100;
            const infraPct = r.total_cost > 0 ? (r.infra_cost / r.total_cost) * 100 : 100;
            return (
              <div key={r.source}>
                <div className="flex items-baseline justify-between text-xs mb-0.5">
                  <span className="font-medium text-zinc-700 dark:text-zinc-200">
                    {r.source}
                  </span>
                  <span className="text-zinc-500">
                    {formatInt(r.audit_count)} • {formatUsd(r.total_cost)}
                  </span>
                </div>
                <div
                  className="h-5 rounded overflow-hidden bg-zinc-100 dark:bg-zinc-800 flex"
                  style={{ width: `${totalPct.toFixed(2)}%` }}
                >
                  <div className="h-full bg-blue-500" style={{ width: `${infraPct.toFixed(2)}%` }} />
                  <div className="h-full bg-orange-500 flex-1" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" /> infra
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-orange-500" /> vendor
          </span>
        </span>
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
